import { useForm } from "antd/es/form/Form";
import i18n from "i18next";
import { useState } from "react";
import { useDispatch } from "react-redux";

import type { FormMutation } from "../../../core/service/types/FormMutation";
import { showSuccessNotification, showWarningNotification } from "../../../core/setup/redux/state/notifications/notificationsState";
import type { Entity, ID } from "../../../core/types/fe";
import type { ImportResult } from "../../FeatureButtons/types";

type BatchUpdateFilters = Entity & { ids?: Array<ID> };

type BatchUpdateTrigger<TFilters extends BatchUpdateFilters> = (
    arg: FormMutation<TFilters>,
) => Promise<{ data?: ImportResult; error?: unknown }>;

// Shared drawer-open state, id extraction, and success/error notification handling behind every
// "select rows -> fill a form -> batch update them" button (Site/VirtualSite/Terminal batch update).
export const useBatchUpdateDrawer = <TFilters extends BatchUpdateFilters, TItem extends Entity>(
    items: TItem[] | undefined,
    isDisabled: boolean | undefined,
    saveFormData: BatchUpdateTrigger<TFilters>,
) => {
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = useForm();
    const dispatch = useDispatch();

    const isDisabledFinal = isDisabled || (items?.length ?? 0) < 2;

    const showDrawer = () => setOpen(true);
    const onClose = () => setOpen(false);

    const onSubmit = async (filters: TFilters) => {
        setIsSubmitting(true);
        try {
            const ids = items?.map((item) => item.id).filter((id): id is ID => id !== undefined);
            const result = await saveFormData({ data: { ...filters, ids } });
            const hasError = "error" in result;
            const importErrors: string[] = result?.data?.importErrors ?? [];

            if (importErrors.length) {
                const translatedErrors = importErrors
                    .slice(0, 6) // cap to max 6, safe for shorter arrays
                    .map((key) => i18n.t(key))
                    .filter((msg) => !!msg && msg.trim().length > 0);

                dispatch(
                    showWarningNotification({
                        text: translatedErrors.length > 0 ? translatedErrors.join("\n") : i18n.t("common.batchUpdateError"),
                        autoCloseDisabled: true,
                    }),
                );
                onClose();
                return;
            }

            if (!hasError) {
                const successCount = result?.data?.importSuccessCount ?? 0;
                const failedCount = result?.data?.importFailedCount ?? 0;

                dispatch(
                    showSuccessNotification({
                        text: i18n.t("list.batchUpdate.success.more", {
                            0: `${successCount}`,
                            1: `${successCount + failedCount}`,
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                onClose();
                form.resetFields();
            }
        } catch {
            // Error handling is managed by RTK Query error in result
        } finally {
            setIsSubmitting(false);
        }
    };

    return { open, form, isDisabledFinal, isSubmitting, showDrawer, onClose, onSubmit };
};
